import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { QRCodeSVG } from "qrcode.react";
import { Check, Copy, Loader2, ShieldCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CodeInput } from "@/components/shared/CodeInput";
import { serverMessage } from "@/lib/apiError";
import {
  confirmTotpEnrollment,
  getTotpStatus,
  startTotpEnrollment,
  type TotpStatus,
} from "@/services/totpService";

/**
 * The enrollment flow itself: scan, confirm, and the recovery codes.
 *
 * ONE copy, rendered by two callers — the /security/two-factor page and the
 * blocking gate that covers the app when enrollment is mandatory. They differ only
 * in their frame, and a second copy is how one of them quietly loses the
 * acknowledgement checkbox on the codes step.
 *
 * The codes step is the one that matters most and the easiest to get wrong:
 * they are returned exactly ONCE by the confirm call and no endpoint returns
 * them again, so this must not let someone past them casually. Hence the
 * explicit acknowledgement rather than a plain Continue.
 *
 * Spec 5d9018f9, control C-5.
 */
type Stage = "loading" | "already" | "scan" | "codes";

export function TwoFactorEnrollment({
  onDone,
}: {
  /** Where the caller goes once there is nothing left to do here. */
  onDone: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [stage, setStage] = useState<Stage>("loading");
  const [status, setStatus] = useState<TotpStatus | null>(null);
  const [secret, setSecret] = useState("");
  const [otpauthUri, setOtpauthUri] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [acknowledged, setAcknowledged] = useState(false);
  const [copied, setCopied] = useState(false);

  const begin = useCallback(async (): Promise<void> => {
    try {
      const started = await startTotpEnrollment();
      setSecret(started.secret);
      setOtpauthUri(started.otpauthUri);
      setStage("scan");
    } catch (err: unknown) {
      // 409 means a confirmed credential already exists. Not an error worth a
      // toast — it is a state, and the already-configured view is the answer.
      const code409 = (err as { response?: { status?: number } })?.response?.status === 409;
      if (code409) {
        setStage("already");
        return;
      }
      toast.error(serverMessage(err) ?? t("security.twoFactor.startError"));
      setStage("already");
    }
  }, [t]);

  useEffect(() => {
    void (async () => {
      try {
        const current = await getTotpStatus();
        setStatus(current);
        if (current.enrolled) {
          setStage("already");
          return;
        }
        await begin();
      } catch (err: unknown) {
        toast.error(serverMessage(err) ?? t("security.twoFactor.loadError"));
        setStage("already");
      }
    })();
  }, [begin, t]);

  const handleConfirm = async (): Promise<void> => {
    const digits = code.replace(/\D/g, "").slice(0, 6);
    if (digits.length !== 6) {
      toast.error(t("security.twoFactor.codeInvalid"));
      return;
    }
    setBusy(true);
    try {
      const result = await confirmTotpEnrollment(digits);
      setRecoveryCodes(result.recoveryCodes);
      setStage("codes");
    } catch (err: unknown) {
      // Surfacing the server's reason rather than one blanket string: at this
      // point the caller is already authenticated as the account being
      // enrolled, so "that code is not valid" versus "start enrollment first"
      // leaks nothing and is the only actionable part of the response. This is
      // NOT the login path, where one generic failure is deliberate (C-3).
      toast.error(serverMessage(err) ?? t("security.twoFactor.codeRejected"));
    } finally {
      setBusy(false);
    }
  };

  const copyCodes = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join("\n"));
      setCopied(true);
      toast.success(t("security.twoFactor.copied"));
    } catch {
      // Clipboard access is denied in some browsers and contexts. The codes
      // are on screen either way, so this must not read as a failure to
      // generate them.
      toast.error(t("security.twoFactor.copyFailed"));
    }
  };

  const downloadCodes = (): void => {
    const blob = new Blob([`${recoveryCodes.join("\n")}\n`], {
      type: "text/plain;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "gestionalo-recovery-codes.txt";
    link.click();
    URL.revokeObjectURL(url);
  };

  if (stage === "loading") {
    return (
      <div className="flex min-h-[12rem] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
      </div>
    );
  }

  if (stage === "already") {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-emerald-600" aria-hidden />
          <h1 className="text-xl font-semibold">
            {t("security.twoFactor.alreadyTitle")}
          </h1>
        </div>
        <p className="text-muted-foreground">{t("security.twoFactor.alreadyBody")}</p>
        {status != null && status.enrolled ? (
          <p className="text-sm text-muted-foreground">
            {t("security.twoFactor.recoveryRemaining", {
              count: status.unusedRecoveryCodes,
            })}
          </p>
        ) : null}
        <Button onClick={onDone}>{t("security.twoFactor.done")}</Button>
      </div>
    );
  }

  if (stage === "codes") {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-xl font-semibold">{t("security.twoFactor.codesTitle")}</h1>
        {/* Stated before the codes, not after: someone who reads one line
            should read the line that says these are shown once. */}
        <p className="text-muted-foreground">{t("security.twoFactor.codesWarning")}</p>
        <ul className="grid grid-cols-2 gap-2 rounded-lg bg-muted/40 p-4 font-mono text-sm">
          {recoveryCodes.map((recoveryCode) => (
            <li key={recoveryCode} className="tracking-widest">
              {recoveryCode}
            </li>
          ))}
        </ul>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => void copyCodes()} className="flex-1">
            {copied ? (
              <Check className="mr-2 h-4 w-4" aria-hidden />
            ) : (
              <Copy className="mr-2 h-4 w-4" aria-hidden />
            )}
            {t("security.twoFactor.copy")}
          </Button>
          <Button variant="outline" onClick={downloadCodes} className="flex-1">
            {t("security.twoFactor.download")}
          </Button>
        </div>
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-1"
            checked={acknowledged}
            onChange={(ev) => setAcknowledged(ev.target.checked)}
          />
          <span>{t("security.twoFactor.acknowledge")}</span>
        </label>
        <Button disabled={!acknowledged} onClick={onDone}>
          {t("security.twoFactor.done")}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-xl font-semibold">{t("security.twoFactor.title")}</h1>
      <p className="text-muted-foreground">{t("security.twoFactor.intro")}</p>

      {otpauthUri !== "" ? (
        <div className="flex justify-center rounded-lg bg-white p-4">
          <QRCodeSVG value={otpauthUri} size={200} level="M" includeMargin />
        </div>
      ) : null}

      {/* The manual seed is NOT a fallback for a broken QR — it is the only
          route for anyone enrolling on the same device the authenticator
          runs on, which is most people on a phone. */}
      <div className="grid gap-2">
        <Label htmlFor="totp-secret">{t("security.twoFactor.secretLabel")}</Label>
        <div className="flex gap-2">
          <Input
            id="totp-secret"
            readOnly
            value={secret}
            className="font-mono tracking-widest"
            onFocus={(ev) => ev.currentTarget.select()}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              void navigator.clipboard
                .writeText(secret)
                .then(() => toast.success(t("security.twoFactor.copied")))
                .catch(() => toast.error(t("security.twoFactor.copyFailed")));
            }}
          >
            <Copy className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      </div>

      <div className="grid gap-2">
        <Label htmlFor="totp-confirm-code">{t("security.twoFactor.codeLabel")}</Label>
        <CodeInput
          id="totp-confirm-code"
          value={code}
          onChange={setCode}
          disabled={busy}
        />
      </div>

      <Button
        onClick={() => void handleConfirm()}
        disabled={busy || code.replace(/\D/g, "").length !== 6}
      >
        {busy ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
            {t("security.twoFactor.confirming")}
          </>
        ) : (
          t("security.twoFactor.confirm")
        )}
      </Button>
    </div>
  );
}
